import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Box,
  Typography,
  List,
  ListItem,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Divider,
  Skeleton,
  Fade,
  MenuItem,
} from "@mui/material";
import {
  SearchOutlined as SearchIcon,
  CloseOutlined as CloseIcon,
  CloseCircleFilled as ClearIcon,
  BankOutlined as ApartmentIcon,
  CheckCircleFilled as CheckCircleIcon,
  EnvironmentOutlined as LocationOnIcon,
  ArrowLeftOutlined as ArrowBackIcon,
  ArrowRightOutlined as ArrowForwardIcon,
  SwapOutlined as SwapHorizIcon,
  TeamOutlined as TeamIcon,
  NumberOutlined as NumberIcon,
  FlagOutlined as StageIcon,
} from "@ant-design/icons";
import HttpService from "../services/httpService";

const BRAND_GRADIENT = "linear-gradient(135deg, #1C375D 0%, #2a5ea8 100%)";

// Derives up to two initials from an EC's name for its avatar, e.g.
// "HSR Layout" -> "HL", "Jayanagar" -> "JA".
const getInitials = (name = "") => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "EC";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

// Small pill used by the header to show which of the two steps is active.
const StepPill = ({ index, label, active, done }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 0.75,
      px: 1.25,
      py: 0.5,
      borderRadius: 5,
      fontSize: 12,
      fontWeight: 600,
      bgcolor: active ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.08)",
      color: active ? "#fff" : "rgba(255,255,255,0.65)",
      transition: "all .2s ease",
    }}
  >
    <Box
      sx={{
        width: 18,
        height: 18,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: done ? "#4CD787" : active ? "#fff" : "rgba(255,255,255,0.2)",
        color: done ? "#fff" : active ? "#1C375D" : "rgba(255,255,255,0.7)",
        fontSize: 11,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {done ? <CheckCircleIcon style={{ fontSize: 11 }} /> : index}
    </Box>
    {label}
  </Box>
);

// Two-step flow: "select" an Experience Center (searchable card list, scoped
// to a city - defaulting to the lead's current city, but switchable) ->
// "confirm" a short review before actually submitting, since this reassigns
// lead ownership, regenerates the lead number, and resets the stage - not
// something to fire off on a single accidental click. Changing the city here
// only changes which ECs are offered - it never updates the lead's own
// city/location, only its experienceCenterId once an EC is actually assigned.
// The current EC is always excluded from the list.
const AssignToECDialog = ({
  open,
  onClose,
  leadId,
  currentUniqueLeadNumber,
  currentExperienceCenterId,
  currentExperienceCenterName,
  currentExperienceCenterCode,
  currentLocationId,
  onAssignSuccess,
}) => {
  const [experienceCenters, setExperienceCenters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState("");
  const [searchText, setSearchText] = useState("");
  const [selectedEc, setSelectedEc] = useState(null);
  const [step, setStep] = useState("select");
  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState("");

  // Every time the dialog is opened, default the city picker back to the
  // lead's current city (rather than remembering whatever was picked last).
  useEffect(() => {
    if (open) {
      setSelectedLocationId(currentLocationId || "");
      fetchLocations();
    }
  }, [open, currentLocationId]);

  useEffect(() => {
    if (open && selectedLocationId) {
      fetchExperienceCenters(selectedLocationId);
    }
  }, [open, selectedLocationId]);

  const fetchLocations = async () => {
    setLocationsLoading(true);
    try {
      const result = await HttpService.getLocation();
      setLocations(result?.data || []);
    } catch (err) {
      console.error("Error fetching locations:", err);
      // Non-fatal - the dropdown just falls back to showing only the
      // current city (still resolvable via currentExperienceCenterName).
    } finally {
      setLocationsLoading(false);
    }
  };

  const fetchExperienceCenters = async (locationId) => {
    setLoading(true);
    setError("");
    try {
      const result = await HttpService.getExpericenCentersByLocation(locationId);
      const centers = result?.data || [];
      // Never let the user reassign a lead to the EC it's already on.
      setExperienceCenters(
        centers.filter((ec) => String(ec._id) !== String(currentExperienceCenterId))
      );
    } catch (err) {
      // The backend 404s when a city has no Experience Centers at all -
      // that's a legitimate empty state, not a failure worth alarming over.
      if (err.response?.status === 404) {
        setExperienceCenters([]);
      } else {
        console.error("Error fetching experience centers:", err);
        setError("Failed to load Experience Centers");
      }
    } finally {
      setLoading(false);
    }
  };

  const selectedLocationName = useMemo(
    () => locations.find((loc) => String(loc._id) === String(selectedLocationId))?.name,
    [locations, selectedLocationId]
  );
  const isCurrentCity = String(selectedLocationId) === String(currentLocationId);

  const filteredCenters = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) return experienceCenters;
    return experienceCenters.filter((ec) => {
      return (
        ec.name?.toLowerCase().includes(query) ||
        ec.code?.toLowerCase().includes(query)
      );
    });
  }, [experienceCenters, searchText]);

  const resetState = () => {
    setSearchText("");
    setSelectedEc(null);
    setStep("select");
    setError("");
  };

  const handleClose = () => {
    if (!assigning) {
      resetState();
      onClose();
    }
  };

  const handleSelectEc = (ec) => {
    setSelectedEc(ec);
    setError("");
  };

  const handleLocationChange = (e) => {
    setSelectedLocationId(e.target.value);
    // A city switch invalidates whatever was picked for the previous city.
    setSelectedEc(null);
    setSearchText("");
    setError("");
  };

  const handleGoToConfirm = () => {
    if (!selectedEc) {
      setError("Please select an Experience Center");
      return;
    }
    setError("");
    setStep("confirm");
  };

  const handleAssign = async () => {
    if (!selectedEc) return;
    setAssigning(true);
    setError("");
    try {
      const response = await HttpService.assignLeadToExperienceCenter({
        leadId,
        experienceCenterId: selectedEc._id,
      });
      if (response.data.success) {
        if (onAssignSuccess) {
          onAssignSuccess(response.data.data);
        }
        resetState();
        onClose();
      } else {
        setError(response.data.message || "Failed to assign lead to Experience Center");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to assign lead to Experience Center");
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          minWidth: { xs: "90%", sm: "500px" },
          overflow: "hidden",
        },
      }}
    >
      {/* Gradient header with an icon badge, current-EC context, and a
          lightweight step indicator so the two-step flow reads clearly. */}
      <Box
        sx={{
          background: BRAND_GRADIENT,
          color: "#fff",
          px: 3,
          pt: 2.5,
          pb: 2,
          position: "relative",
        }}
      >
        <IconButton
          size="small"
          onClick={handleClose}
          disabled={assigning}
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            color: "rgba(255,255,255,0.85)",
            "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
          }}
        >
          <CloseIcon style={{ fontSize: 14 }} />
        </IconButton>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255,255,255,0.16)",
              flexShrink: 0,
            }}
          >
            <SwapHorizIcon style={{ color: "#fff", fontSize: 22 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" fontSize="1.05rem" fontWeight={700} sx={{ color: "#fff" }}>
              {step === "select" ? "Transfer Experience Center" : "Review Assignment"}
            </Typography>
            {currentExperienceCenterName && (
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.8)" }}>
                Currently at {currentExperienceCenterName}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
          <StepPill index={1} label="Select EC" active={step === "select"} done={step === "confirm"} />
          <StepPill index={2} label="Confirm" active={step === "confirm"} done={false} />
        </Box>
      </Box>

      <DialogContent sx={{ pt: 2.5, pb: 1, bgcolor: "#fafbfd" }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, fontSize: "0.875rem", borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <Fade in={step === "select"} unmountOnExit>
          <Box sx={{ display: step === "select" ? "block" : "none" }}>
            <TextField
              select
              label="City"
              size="small"
              fullWidth
              value={selectedLocationId}
              onChange={handleLocationChange}
              disabled={locationsLoading || locations.length === 0}
              sx={{
                mb: 1.5,
                bgcolor: "#fff",
                "& .MuiOutlinedInput-root": { borderRadius: 3 },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOnIcon style={{ fontSize: 15, color: "rgba(0,0,0,0.4)" }} />
                  </InputAdornment>
                ),
              }}
              helperText={isCurrentCity ? "Lead's current city" : `Showing ECs in ${selectedLocationName || "selected city"}`}
            >
              {locations.map((loc) => (
                <MenuItem key={loc._id} value={loc._id}>
                  {loc.name}
                  {String(loc._id) === String(currentLocationId) ? " (Current)" : ""}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              placeholder="Search by name or code..."
              variant="outlined"
              size="small"
              fullWidth
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{
                mb: 2,
                bgcolor: "#fff",
                "& .MuiOutlinedInput-root": { borderRadius: 3 },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ fontSize: 15, color: "rgba(0,0,0,0.4)" }} />
                  </InputAdornment>
                ),
                endAdornment: searchText && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchText("")}>
                      <ClearIcon style={{ fontSize: 14, color: "rgba(0,0,0,0.35)" }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.25, px: 0.25 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontSize: "0.78rem", color: "text.secondary", display: "flex", alignItems: "center", gap: 0.5, fontWeight: 600 }}
              >
                <LocationOnIcon style={{ fontSize: 12 }} />
                {isCurrentCity ? "Other Experience Centers in this city" : `Experience Centers in ${selectedLocationName || "this city"}`}
              </Typography>
              {selectedLocationId && !loading && (
                <Chip
                  label={filteredCenters.length}
                  size="small"
                  sx={{ height: 20, minWidth: 26, fontSize: 11, fontWeight: 700, bgcolor: "#e7edf7", color: "#1C375D" }}
                />
              )}
            </Box>

            {!selectedLocationId ? (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={5}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    bgcolor: "#eef1f6",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1.5,
                  }}
                >
                  <LocationOnIcon style={{ fontSize: 28, color: "#a9b4c6" }} />
                </Box>
                <Typography variant="body2" fontWeight={600} color="text.secondary">
                  Pick a city above to see its Experience Centers
                </Typography>
              </Box>
            ) : loading ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} variant="rounded" height={62} sx={{ borderRadius: 2 }} animation="wave" />
                ))}
              </Box>
            ) : filteredCenters.length === 0 ? (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={5}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    bgcolor: "#eef1f6",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1.5,
                  }}
                >
                  <ApartmentIcon style={{ fontSize: 28, color: "#a9b4c6" }} />
                </Box>
                <Typography variant="body2" fontWeight={600} color="text.secondary">
                  {experienceCenters.length === 0
                    ? isCurrentCity
                      ? "No other Experience Centers found in this city"
                      : `No Experience Centers found in ${selectedLocationName || "this city"}`
                    : "No Experience Centers match your search"}
                </Typography>
                {experienceCenters.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    Try a different name or code
                  </Typography>
                )}
              </Box>
            ) : (
              <List sx={{ p: 0, maxHeight: 320, overflowY: "auto", pr: 0.5 }}>
                {filteredCenters.map((ec) => {
                  const isSelected = selectedEc?._id === ec._id;
                  return (
                    <ListItem
                      key={ec._id}
                      onClick={() => handleSelectEc(ec)}
                      sx={{
                        border: "1px solid",
                        borderColor: isSelected ? "#2a5ea8" : "#e6e9f0",
                        borderRadius: 2.5,
                        mb: 1,
                        p: 1.25,
                        cursor: "pointer",
                        bgcolor: isSelected ? "#eef3fa" : "#fff",
                        boxShadow: isSelected ? "0 2px 10px rgba(42,94,168,0.15)" : "0 1px 2px rgba(16,24,40,0.03)",
                        transition: "all .15s ease",
                        "&:hover": {
                          bgcolor: isSelected ? "#eef3fa" : "#f7f9fc",
                          borderColor: "#2a5ea8",
                          transform: "translateY(-1px)",
                          boxShadow: "0 4px 12px rgba(28,55,93,0.1)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: isSelected ? BRAND_GRADIENT : "#f0f3f8",
                          color: isSelected ? "#fff" : "#5a6b85",
                          mr: 1.5,
                          flexShrink: 0,
                          fontSize: 13,
                          fontWeight: 700,
                          transition: "all .15s ease",
                        }}
                      >
                        {ec.code || getInitials(ec.name)}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {ec.name}
                        </Typography>
                        {ec.code && (
                          <Typography variant="caption" color="text.secondary">
                            Code: {ec.code}
                          </Typography>
                        )}
                      </Box>
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: isSelected ? "#2a5ea8" : "transparent",
                          border: isSelected ? "none" : "2px solid #d8dee8",
                          flexShrink: 0,
                          transition: "all .15s ease",
                        }}
                      >
                        {isSelected && <CheckCircleIcon style={{ color: "#fff", fontSize: 22 }} />}
                      </Box>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
        </Fade>

        <Fade in={step === "confirm"} unmountOnExit>
          <Box sx={{ display: step === "confirm" ? "block" : "none" }}>
            {selectedEc && (
              <>
                {/* Visual from -> to summary */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    bgcolor: "#fff",
                    border: "1px solid #e6e9f0",
                    borderRadius: 3,
                    p: 2,
                    mb: 2,
                  }}
                >
                  <Box sx={{ textAlign: "center", flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                      FROM
                    </Typography>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        bgcolor: "#f0f3f8",
                        color: "#8a94a6",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: 0.5,
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {currentExperienceCenterCode || getInitials(currentExperienceCenterName)}
                    </Box>
                    <Typography variant="body2" fontWeight={600} noWrap color="text.secondary">
                      {currentExperienceCenterName || "N/A"}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      bgcolor: "#e7edf7",
                      color: "#2a5ea8",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      mx: 1,
                    }}
                  >
                    <ArrowForwardIcon style={{ fontSize: 16 }} />
                  </Box>

                  <Box sx={{ textAlign: "center", flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" sx={{ display: "block", mb: 0.5, color: "#2a5ea8", fontWeight: 700 }}>
                      TO
                    </Typography>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        background: BRAND_GRADIENT,
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: 0.5,
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {selectedEc.code || getInitials(selectedEc.name)}
                    </Box>
                    <Typography variant="body2" fontWeight={700} noWrap>
                      {selectedEc.name}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ border: "1px solid #e6e9f0", borderRadius: 3, bgcolor: "#fff", p: 2, mb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 0.75 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: "text.secondary" }}>
                      <NumberIcon style={{ fontSize: 14 }} />
                      <Typography variant="body2" color="text.secondary">Unique Lead Number</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <Typography variant="body2" sx={{ textDecoration: "line-through", color: "text.disabled" }}>
                        {currentUniqueLeadNumber || "N/A"}
                      </Typography>
                      <ArrowForwardIcon style={{ fontSize: 11, color: "#a9b4c6" }} />
                      <Typography variant="body2" fontStyle="italic" color="#2a5ea8" fontWeight={600}>
                        New
                      </Typography>
                    </Box>
                  </Box>
                  <Divider />
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 0.75 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: "text.secondary" }}>
                      <StageIcon style={{ fontSize: 14 }} />
                      <Typography variant="body2" color="text.secondary">Current Stage</Typography>
                    </Box>
                    <Chip label="Lead Received" size="small" sx={{ bgcolor: "#e9f9ef", color: "#1e8449", fontWeight: 700 }} />
                  </Box>
                </Box>

                <Alert
                  severity="info"
                  icon={<TeamIcon style={{ fontSize: 18 }} />}
                  sx={{ fontSize: "0.8rem", borderRadius: 2 }}
                >
                  The ASM (or Sales Manager, if no ASM is available) for{" "}
                  <b>{selectedEc.name}</b> will automatically be assigned as the new lead owner.
                </Alert>
              </>
            )}
          </Box>
        </Fade>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, bgcolor: "#fafbfd" }}>
        {step === "confirm" && (
          <Button
            onClick={() => setStep("select")}
            disabled={assigning}
            size="small"
            startIcon={<ArrowBackIcon style={{ fontSize: 14 }} />}
            sx={{ color: "#5a6b85" }}
          >
            Back
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={assigning}
          size="small"
          sx={{
            color: "#5a6b85",
            borderColor: "#d8dee8",
            "&:hover": { borderColor: "#a9b4c6", bgcolor: "rgba(90,107,133,0.06)" },
          }}
        >
          Cancel
        </Button>
        {step === "select" ? (
          <Button
            onClick={handleGoToConfirm}
            variant="contained"
            disabled={!selectedEc || loading}
            size="small"
            endIcon={<ArrowForwardIcon style={{ fontSize: 14 }} />}
            sx={{
              minWidth: 110,
              borderRadius: 2.5,
              background: BRAND_GRADIENT,
              boxShadow: "none",
              "&:hover": { background: "linear-gradient(135deg, #16294a 0%, #234f8f 100%)", boxShadow: "0 4px 12px rgba(28,55,93,0.25)" },
              "&.Mui-disabled": { background: "#d8dee8", color: "#fff" },
            }}
          >
            Next
          </Button>
        ) : (
          <Button
            onClick={handleAssign}
            variant="contained"
            disabled={assigning}
            size="small"
            sx={{
              minWidth: 140,
              borderRadius: 2.5,
              background: BRAND_GRADIENT,
              boxShadow: "none",
              "&:hover": { background: "linear-gradient(135deg, #16294a 0%, #234f8f 100%)", boxShadow: "0 4px 12px rgba(28,55,93,0.25)" },
              "&.Mui-disabled": { background: "#d8dee8", color: "#fff" },
            }}
          >
            {assigning ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Confirm & Assign"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AssignToECDialog;
